import { toast } from "react-toastify"
import { getLessonWithDemo, logLessonView } from "../../domain/lesson"
import { getStandard as fetchStandard, toggleDone as toggleDoneRequest } from "../../domain/question"
import { composeLockMessage } from "../../utils/lockMessage"
import { GOLD_REFUSED_FALLBACK } from "../../utils/gold"

export const LESSONS_SET_SELECTED = "LESSONS_SET_SELECTED"
export const LESSONS_CLEAR_LESSON = "LESSONS_CLEAR_LESSON"
export const LESSONS_SET_FORBIDDEN = "LESSONS_SET_FORBIDDEN"

export const LESSONS_SET_STANDARD = "LESSONS_SET_STANDARD"
export const LESSONS_CHANGE_DONE = "LESSONS_CHANGE_DONE"
export const LESSONS_CLEAR_STANDARD = "LESSONS_CLEAR_STANDARD"

export const LESSONS_SELECTED_REQUEST = "LESSONS_SELECTED_REQUEST"
export const LESSONS_SELECTED_REQUEST_DONE = "LESSONS_SELECTED_REQUEST_DONE"

export const LESSONS_STANDARD_REQUEST = "LESSONS_STANDARD_REQUEST"
export const LESSONS_STANDARD_REQUEST_DONE = "LESSONS_STANDARD_REQUEST_DONE"

export const getLesson = id => dispatch => {
	return getLessonWithDemo(id)
		.then(res => {
			dispatch(setLesson(res.data.data))
			dispatch(lessonSelectedRequestDone())

			// Recorded only once the lesson actually resolved, so a 404 or a
			// forbidden lesson leaves no trace. Fire-and-forget: nothing the
			// student sees depends on it, and it must never delay the lesson or
			// surface an error to them.
			logLessonView(id)

			return res.data.data
		})
		.catch(err => {
			// Locked order (feature/progression-lockdown): a student reaching a
			// locked lesson through an old bookmark or a stale page gets a 403
			// with a message. Show that explanation instead of spinning forever
			// -- any other failure is left exactly as it was (unhandled), which
			// is the existing behaviour this change is not trying to fix.
			const status = err.response && err.response.status
			if (status === 403) {
				// Composed from the two structured halves the server sends, so
				// this screen reads identically to the lesson list. It used to
				// take `message` alone, which was the reason only -- a child
				// following a stale bookmark was challenged ("Want access to
				// this section? Earn it.") without being told the terms.
				// `message` stays the fallback for a payload without them.
				const data = (err.response && err.response.data) || {}
				dispatch(setForbidden(composeLockMessage(data.lock_reason, data.lock_hint) || data.message || null))
				dispatch(lessonSelectedRequestDone())
				return null
			}
			throw err
		})
}
export const setLesson = data => ({ type: LESSONS_SET_SELECTED, payload: data })
export const setForbidden = message => ({ type: LESSONS_SET_FORBIDDEN, payload: message })
export const clearLesson = () => ({ type: LESSONS_CLEAR_LESSON })

export const getStandard = id => dispatch => {
	return fetchStandard(id).then(res => {
		dispatch(setStandard(res.data.data))
		dispatch(lessonStandardRequestDone())
		return res.data.data
	})
}
export const setStandard = data => ({ type: LESSONS_SET_STANDARD, payload: data })
// body (optional) is passed through untouched; see domain/question.js.
export const toggleDone = (id, body) => dispatch => {
	dispatch(changeDone(id))
	toggleDoneRequest(id, body).catch(err => {
		// Put the tick back where it was: the server refused, so nothing on the
		// student's record changed and the optimistic flip is now a lie.
		dispatch(changeDone(id))

		// GE GOLD, and a progression lock, both refuse a GOT IT with a 403 and
		// an explanation (AreaClosures::assertEarnable and Progression::refuse
		// in ../wicked-maths-laravel). Silently un-ticking was the old
		// behaviour, and for a gold section it is the worst possible one: the
		// child taps "Got it!", watches the tick appear and vanish, and is told
		// nothing. The section itself says so up front (GoldNotice), and this is
		// the answer if they try anyway.
		//
		// Anything else stays silent exactly as before -- a network blip must
		// not put an error in front of a child mid-lesson, and a 5xx already
		// raises its own toast in the shared interceptor.
		const status = err.response && err.response.status
		if (status !== 403) return

		const data = (err.response && err.response.data) || {}
		toast.info(
			composeLockMessage(data.closed_reason, data.closed_hint) ||
				composeLockMessage(data.lock_reason, data.lock_hint) ||
				data.message ||
				GOLD_REFUSED_FALLBACK,
		)
	})
}
export const changeDone = questionId => ({ type: LESSONS_CHANGE_DONE, payload: questionId })
export const clearStandard = () => ({ type: LESSONS_CLEAR_STANDARD })

export const lessonSelectedRequest = () => ({ type: LESSONS_SELECTED_REQUEST })
export const lessonSelectedRequestDone = () => ({ type: LESSONS_SELECTED_REQUEST_DONE })

export const lessonStandardRequest = () => ({ type: LESSONS_STANDARD_REQUEST })
export const lessonStandardRequestDone = () => ({ type: LESSONS_STANDARD_REQUEST_DONE })
